/**
 * 客服联系（09 帮助中心的子页）
 * -----------------------------------------------------------------------------
 * 一期客服为**人工承接**（不做自动应答），由运营人员在公众号内直接回复。
 *
 * 客服二维码来自后台「客服二维码管理」的**登记**内容（C-10：只登记，不采集，
 * 不调企微 API），经 `GET /api/h5/support/qrcode`（公开只读）下发。
 * 本页「先给码、再给路径」：取到码就展示，取不到就只给文字兜底 —— 客服入口的
 * 价值在「能找到人」，因此**不渲染裂图、不弹错误页**。
 *
 * 取不到码是**正常业务分支**，不是异常：后台未配置缺省码 / 码被停用 / 图片丢失
 * 都会返回 `SUPPORT-004`（404）。
 *
 * 区域匹配由服务端在会话内推导（`resolveForStudent` + `latestRegistrationRegion`），
 * 优先级 ① query ② 会话推导 ③ 全局缺省 —— 前端不伪造区域（换设备即丢）。
 */
import { ErrorCode } from '@enroll/shared';
import { ApiError } from '../api/client';
import { useSupportQrcode } from '../api/hooks';
import { Card, LoadingBlock, Notice, PageShell } from '../components/layout';

/** 取不到码的原因：`SUPPORT-004` = 后台确实没有可用码；其余为加载/网络失败 */
function unavailableReason(error: unknown): string {
  if (error instanceof ApiError && error.code === ErrorCode.SUPPORT_QRCODE_UNAVAILABLE) {
    return '客服暂时没有可用的二维码，请按下方路径联系我们';
  }
  return '客服二维码加载失败，请按下方路径联系我们';
}

const CONTACT_PATHS = [
  '在公众号对话页直接发送消息，客服会在工作时间内回复',
  '咨询时请提供报名单号，便于客服快速定位',
  '缴费与发票相关咨询，请说明转账时间与金额',
] as const;

export default function ServiceHelp() {
  const qrcode = useSupportQrcode();
  const data = qrcode.data;

  return (
    <PageShell title="联系客服" back solidNav>
      <Card title="客服二维码">
        {qrcode.isPending ? <LoadingBlock text="正在获取客服二维码" /> : null}
        {!qrcode.isPending && data ? (
          <div className="support-qrcode">
            <img className="support-qrcode__image" src={data.imageUrl} alt="客服二维码" />
            <span className="support-qrcode__owner">专属客服：{data.ownerStaff}</span>
            <span className="support-qrcode__hint">长按识别，或保存图片后用微信扫码添加</span>
          </div>
        ) : null}
        {!qrcode.isPending && !data ? (
          <Notice tone="warning">{unavailableReason(qrcode.error)}</Notice>
        ) : null}
      </Card>

      <Card title="联系路径" tight>
        <ul className="bullet-list">
          {CONTACT_PATHS.map((text) => (
            <li key={text} className="bullet-list__item">
              <span className="bullet-list__dot" />
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="服务时间" tight>
        <p className="card__desc">工作日 9:00 - 18:00；节假日消息会在下一个工作日回复。</p>
      </Card>
    </PageShell>
  );
}
